import React, { useEffect } from 'react';
import { FiArrowLeft, FiMail, FiDownload, FiCheck } from 'react-icons/fi';
import * as translator from '../../../../../utils/translator';
import logo_v2025 from '../../../../../images/logo_v2025.png';
import CareProfileCard from './CareProfileCard';

export type ConsultationSummaryStatus = 'processing' | 'completed';

interface DesktopConsultationSummaryOverlayProps {
	isOpen: boolean;
	onClose: () => void;
	language: string;
	title: string;
	doctorName?: string | null;
	dateLabel: string | null;
	timeLabel: string | null;
	status: ConsultationSummaryStatus;
	onMessageDoctor?: () => void;
}

const DesktopConsultationSummaryOverlay: React.FC<DesktopConsultationSummaryOverlayProps> = ({
	isOpen,
	onClose,
	language,
	title,
	doctorName,
	dateLabel,
	timeLabel,
	status,
	onMessageDoctor,
}) => {
	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	return (
		<div id="desktop-consultation-summary-overlay" className="fixed inset-0 z-[1000] h-[100dvh] w-screen bg-[#fdfcf9] overflow-y-auto flex flex-col">
			<header className="sticky top-0 z-10 bg-white border-b border-solid border-[#dee3ed] flex items-center justify-between px-10 py-4 flex-shrink-0">
				<div className="flex items-center gap-x-4">
					<button
						id="desktop-consultation-summary-back"
						type="button"
						onClick={onClose}
						aria-label="Back"
						className="flex items-center justify-center w-8 h-8 border-none bg-transparent text-care-plus-heading cursor-pointer"
					>
						<FiArrowLeft size={18} />
					</button>
					<span className="w-px h-5 bg-[#dee3ed]" />
					<p className="text-[16px] font-semibold text-care-plus-heading">{title}</p>
				</div>

				<img src={logo_v2025} alt="DoktorABC" className="h-[24px] w-auto absolute left-1/2 -translate-x-1/2" />

				<div className="flex items-center gap-x-3">
					<button
						id="desktop-consultation-summary-message-doctor"
						type="button"
						onClick={onMessageDoctor}
						className="flex items-center gap-x-2 rounded-full border border-solid border-[rgba(245,146,0,0.3)] bg-[#fdf8f0] text-care-plus-orange text-[13px] font-medium px-4 py-2 cursor-pointer"
					>
						<FiMail size={15} />
						{translator.translate(language, 'my_account', 'CareProfile/Summary/Header/MessageDoctor')}
					</button>
					<button
						id="desktop-consultation-summary-download-pdf"
						type="button"
						// TODO: no backend PDF export exists yet for consultation summaries
						onClick={() => {}}
						className="flex items-center gap-x-2 rounded-full border border-solid border-[#269c6e] text-[#61a333] text-[13px] font-medium px-4 py-2 cursor-pointer"
					>
						<FiDownload size={15} />
						{translator.translate(language, 'my_account', 'CareProfile/Summary/Header/DownloadPdf')}
					</button>
				</div>
			</header>

			<div className="max-w-[1200px] mx-auto w-full px-10 py-8 flex flex-col gap-y-6">
				<CareProfileCard padding="md" className="flex items-center justify-between flex-wrap gap-y-3">
					<div className="flex items-center gap-x-8 flex-wrap">
						{doctorName && (
							<div className="flex items-baseline gap-x-2">
								<span className="text-sm text-[#616975]">{translator.translate(language, 'my_account', 'CareProfile/Booking/Doctor')}</span>
								<span className="text-[15px] font-semibold text-care-plus-heading">{doctorName}</span>
							</div>
						)}
						{dateLabel && (
							<div className="flex items-baseline gap-x-2">
								<span className="text-sm text-[#616975]">{translator.translate(language, 'my_account', 'CareProfile/Booking/Date')}</span>
								<span className="text-[15px] font-semibold text-care-plus-heading">{dateLabel}</span>
							</div>
						)}
						{timeLabel && (
							<div className="flex items-baseline gap-x-2">
								<span className="text-sm text-[#616975]">{translator.translate(language, 'my_account', 'CareProfile/Booking/Time')}</span>
								<span className="text-[15px] font-semibold text-care-plus-heading">{timeLabel}</span>
							</div>
						)}
					</div>

					<StatusBadge language={language} status={status} />
				</CareProfileCard>

				{status === 'processing' ? <ProcessingState language={language} /> : <CompletedState language={language} />}
			</div>
		</div>
	);
};

const StatusBadge: React.FC<{ language: string; status: ConsultationSummaryStatus }> = ({ language, status }) => {
	if (status === 'completed') {
		return (
			<span className="flex items-center gap-x-1.5 rounded-full bg-[#f4fbee] px-3 py-1.5 text-[12px] font-medium">
				<FiCheck size={14} className="text-[#61a333]" />
				<span className="bg-gradient-to-r from-[#61a333] to-[#39871f] bg-clip-text text-transparent">
					{translator.translate(language, 'my_account', 'CareProfile/Summary/Status/Completed')}
				</span>
			</span>
		);
	}

	return (
		<span className="flex items-center gap-x-1.5 rounded-full bg-[#fdf3e2] px-3 py-1.5 text-[12px] font-medium text-care-plus-orange-dark">
			<span className="w-2 h-2 rounded-full bg-care-plus-orange" />
			{translator.translate(language, 'my_account', 'CareProfile/Summary/Status/Processing')}
		</span>
	);
};

const ProcessingState: React.FC<{ language: string }> = ({ language }) => (
	<CareProfileCard padding="md" className="min-h-[360px] flex flex-col items-center justify-center gap-y-4 py-16">
		<span className="w-9 h-9 rounded-full border-2 border-[#e7e4db] border-t-care-plus-orange animate-spin" />
		<p className="text-[16px] font-semibold text-care-plus-heading text-center max-w-[320px]">
			{translator.translate(language, 'my_account', 'CareProfile/Summary/Pending')}
		</p>
		<p className="text-[14px] text-[#99a3b2] text-center max-w-[320px]">
			{translator.translate(language, 'my_account', 'CareProfile/Summary/ProcessingSubtext')}
		</p>
		<div className="flex flex-col gap-y-2 w-full max-w-[280px] mt-2">
			{[100, 85, 70, 55, 40].map((width, index) => (
				<span key={width} className="h-3 rounded-full bg-[#f5f6f7]" style={{ width: `${width}%`, opacity: 1 - index * 0.15 }} />
			))}
		</div>
	</CareProfileCard>
);

const CompletedState: React.FC<{ language: string }> = ({ language }) => (
	<div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
		<CareProfileCard padding="md" className="flex flex-col gap-y-4">
			<p className="text-[16px] font-semibold text-care-plus-heading">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/BriefSummary/Title')}</p>
			<p className="text-[14px] text-[#616975] leading-[1.5]">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/BriefSummary/Body')}</p>
			<span className="w-full h-px bg-[#e7e4db]" />
			<p className="text-[14px] font-semibold text-care-plus-heading">
				{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/BriefSummary/WhatWeDiscussedTitle')}
			</p>
			{(['Progress', 'Nutrition', 'Wellbeing'] as const).map((key) => (
				<div key={key} className="flex flex-col gap-y-1">
					<p className="text-[13px] font-semibold text-care-plus-heading">
						{translator.translate(language, 'my_account', `CareProfile/Summary/Completed/BriefSummary/Discussed${key}Title`)}
					</p>
					<p className="text-[13px] text-[#616975] leading-[1.5]">
						{translator.translate(language, 'my_account', `CareProfile/Summary/Completed/BriefSummary/Discussed${key}Body`)}
					</p>
				</div>
			))}
		</CareProfileCard>

		<CareProfileCard padding="md" className="flex flex-col gap-y-4">
			<p className="text-[16px] font-semibold text-care-plus-heading">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Recommendations/Title')}</p>
			<div className="flex flex-col gap-y-1">
				<p className="text-[13px] font-semibold text-care-plus-heading">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Recommendations/MainGoalTitle')}</p>
				<p className="text-[13px] text-[#616975] leading-[1.5]">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Recommendations/MainGoalBody')}</p>
			</div>
			<p className="text-[13px] font-semibold text-care-plus-heading">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Recommendations/ActionsTitle')}</p>
			<div className="flex flex-col gap-y-2">
				{(['Action1', 'Action2', 'Action3'] as const).map((key, index) => (
					<div key={key} className="flex items-start gap-x-2">
						<span className="flex-shrink-0 w-[22px] h-[22px] rounded-full border border-solid border-[#039814] text-[#039814] text-[11px] font-semibold flex items-center justify-center">
							{index + 1}
						</span>
						<p className="text-[13px] text-[#616975] leading-[1.5]">{translator.translate(language, 'my_account', `CareProfile/Summary/Completed/Recommendations/${key}`)}</p>
					</div>
				))}
			</div>
			<p className="text-[13px] font-semibold text-care-plus-heading">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Recommendations/MiniGoalsTitle')}</p>
			<ul className="list-disc pl-4 flex flex-col gap-y-1">
				{(['MiniGoalWeight', 'MiniGoalProtein', 'MiniGoalActivity', 'MiniGoalLogging'] as const).map((key) => (
					<li key={key} className="text-[13px] text-[#616975] leading-[1.5]">
						{translator.translate(language, 'my_account', `CareProfile/Summary/Completed/Recommendations/${key}`)}
					</li>
				))}
			</ul>
		</CareProfileCard>

		<CareProfileCard padding="md" className="flex flex-col gap-y-4">
			<p className="text-[16px] font-semibold text-care-plus-heading">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Monitor/Title')}</p>
			<p className="text-[13px] text-[#616975]">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Monitor/Intro')}</p>
			<ul className="list-disc pl-4 flex flex-col gap-y-1">
				{(['Nausea', 'Constipation', 'Appetite', 'Dizziness', 'NoWeightLoss'] as const).map((key) => (
					<li key={key} className="text-[13px] text-[#616975] leading-[1.5]">
						{translator.translate(language, 'my_account', `CareProfile/Summary/Completed/Monitor/${key}`)}
					</li>
				))}
			</ul>
			<div className="bg-[#f4fbee] border border-solid border-[rgba(117,190,61,0.2)] rounded-2xl p-4">
				<p className="text-[13px] text-[#39871f] leading-[1.5]">{translator.translate(language, 'my_account', 'CareProfile/Summary/Completed/Monitor/Alert')}</p>
			</div>
			<button
				id="desktop-consultation-summary-download-pdf-cta"
				type="button"
				// TODO: no backend PDF export exists yet for consultation summaries
				onClick={() => {}}
				className="w-full rounded-full border border-solid border-[#269c6e] text-[#61a333] text-[13px] font-medium py-2.5 bg-transparent cursor-pointer flex items-center justify-center gap-x-2"
			>
				<FiDownload size={15} />
				{translator.translate(language, 'my_account', 'CareProfile/Summary/Header/DownloadPdf')}
			</button>
		</CareProfileCard>
	</div>
);

export default DesktopConsultationSummaryOverlay;
